import { Badge, Typography } from 'antd';
import styled, { css, FlattenSimpleInterpolation } from 'styled-components';
import { CLEANING_AREA_SELECTION_MAX_HEIGHT } from '../CleaningZones.styles';
import type { CleaningAreaMapOverlayStyle } from '../../../../../../../types/CleaningAreaMapOverlay.types';

export const CleaningAreaMapOverlayContainer = styled.div`
  ${(): FlattenSimpleInterpolation => css`
    position: relative;
    display: flex;
    align-items: flex-start;
    justify-content: center;
    flex: 1;
    min-width: 0;
    /* No fixed height — as tall as the taller of the map and the list; capped for a portrait map. */
    max-height: ${CLEANING_AREA_SELECTION_MAX_HEIGHT}px;
    overflow: hidden;
  `}
`;

/** Sized to the map image's aspect ratio, so percentage-based outlines/labels line up with it. */
export const CleaningAreaMapOverlayFrame = styled.div<{ $aspectRatio?: number }>`
  ${({ $aspectRatio }): FlattenSimpleInterpolation => css`
    position: relative;

    ${$aspectRatio
      ? css`
          /* Width-anchored, top-aligned with the area list; height comes from the ratio. */
          width: 100%;
          height: auto;
          max-height: ${CLEANING_AREA_SELECTION_MAX_HEIGHT}px;
          aspect-ratio: ${$aspectRatio};
        `
      : css`
          width: 100%;
          height: 100%;
        `}
  `}
`;

export const CleaningAreaMapOverlayImage = styled.img`
  display: block;
  width: 100%;
  height: 100%;
  object-fit: contain;
  /* Disables the native image-drag (mousedown-then-move was starting one on the area overlay). */
  -webkit-user-drag: none;
  user-select: none;
`;

export const CleaningAreaOutlineSvg = styled.svg`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
`;

// Sits under CleaningAreaOutlineSvg so the selectable RoomArea outlines stay on top / clickable.
export const CleaningFeatureSvg = styled.svg`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
`;

export const CleaningNoGoAreaPolygon = styled.polygon`
  ${({ theme: { kcc } }): FlattenSimpleInterpolation => css`
    fill: ${kcc.colors.RED[925]}26;
    stroke: ${kcc.colors.RED[925]};
    stroke-width: 3.2px;
    /* Keeps the border a literal 3.2 screen px regardless of the SVG's 100x100 viewBox scaling. */
    vector-effect: non-scaling-stroke;
  `}
`;

export const CleaningSpecialZonePolygon = styled.polygon`
  ${({ theme: { kcc } }): FlattenSimpleInterpolation => css`
    fill: ${kcc.colors.PETROL[500]}26;
    stroke: ${kcc.colors.PETROL[500]};
    stroke-width: 3.2px;
    vector-effect: non-scaling-stroke;
  `}
`;

// Matches the no-go zone's border colour.
export const CleaningNoGoLine = styled.line`
  ${({ theme: { kcc } }): FlattenSimpleInterpolation => css`
    stroke: ${kcc.colors.RED[925]};
    stroke-width: 0.8;
    stroke-linecap: round;
  `}
`;

// Matches the special zone's border colour.
export const CleaningGlassLine = styled.line`
  ${({ theme: { kcc } }): FlattenSimpleInterpolation => css`
    stroke: ${kcc.colors.PETROL[500]};
    stroke-width: 0.8;
    stroke-linecap: round;
  `}
`;

export const CleaningFeatureMarker = styled.div<{ $rotationDeg?: number }>`
  ${({ $rotationDeg = 0 }): FlattenSimpleInterpolation => css`
    position: absolute;
    transform: translate(-50%, -50%) rotate(${$rotationDeg}deg);
    display: flex;
    align-items: center;
    pointer-events: none;
  `}
`;

/* Positioning shell for the start marker's stacked, overlapping layers. */
export const CleaningStartMarkerContainer = styled.div`
  position: relative;
  width: 42px;
  height: 42px;
`;

const startMarkerLayer = css`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  display: flex;
  align-items: center;
  justify-content: center;
`;

/* Semi-transparent so it doesn't fully hide a feature the marker lands on. */
export const CleaningStartMarkerHomeIcon = styled.div`
  ${({ theme: { kcc } }): FlattenSimpleInterpolation => css`
    ${startMarkerLayer}
    width: 38px;
    height: 38px;
    border-radius: 0;
    background-color: ${kcc.colors.BLACK}B3;
    color: #ffffff;

    .svg-icon {
      width: 28px;
      height: 28px;
    }
  `}
`;

/* Sits between the home icon and the KIRA CV50 icon on top of it. */
export const CleaningStartMarkerGlow = styled.div`
  ${({ theme: { kcc } }): FlattenSimpleInterpolation => css`
    ${startMarkerLayer}
    width: 28px;
    height: 28px;
    border-radius: 50%;
    background-color: ${kcc.colors.PRIMARY_KARCHER}66;
  `}
`;

export const CleaningStartMarkerKiraIcon = styled.div`
  ${startMarkerLayer}

  .svg-icon {
    width: 18px;
    height: 18px;
  }
`;

export const CleaningSpecialZoneBadge = styled.div`
  ${({ theme: { kcc } }): FlattenSimpleInterpolation => css`
    display: flex;
    align-items: center;
    gap: 4px;
    font-size: ${kcc.typography.FONT_SIZE.SM}px;
    font-weight: ${kcc.typography.FONT_WEIGHT.EXTRA_BOLD};
    line-height: normal;
    white-space: nowrap;
  `}
`;

/* Passes count. */
export const CleaningSpecialZoneTimes = styled.span`
  ${({ theme: { kcc } }): FlattenSimpleInterpolation => css`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background-color: ${kcc.colors.PETROL[500]};
    color: #ffffff;
    font-size: ${kcc.typography.FONT_SIZE.SM}px;
  `}
`;

/* Suction mode icon (normalMode / ecoMode) — icon fill is currentColor, set to white here. */
export const CleaningSpecialZoneMode = styled.span`
  ${({ theme: { kcc } }): FlattenSimpleInterpolation => css`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background-color: ${kcc.colors.PETROL[500]};
    color: #ffffff;

    .svg-icon {
      width: 14px;
      height: 14px;
    }
  `}
`;

export const CleaningAreaOutlinePolygon = styled.polygon<{
  $isSelected?: boolean;
  $areaStyle?: CleaningAreaMapOverlayStyle;
  $disabled?: boolean;
}>`
  ${({ theme: { kcc }, $isSelected, $areaStyle, $disabled }): FlattenSimpleInterpolation => css`
    /* #4B896980 is a one-off translucent fill only used for this selected-area overlay — not a KCC token. */
    fill: ${$isSelected ? '#4B896980' : 'none'};
    /* rgba(183, 183, 183, 1) is a one-off whole-map outline colour — not a KCC token. */
    stroke: ${$areaStyle === 'wholeMap' ? 'rgba(183, 183, 183, 1)' : kcc.colors.GREEN.AREA_OUTLINE};
    ${$areaStyle === 'wholeMap'
      ? css`
          /* The viewBox is stretched (preserveAspectRatio="none"), so draw the 8px-4px dashed border in
             screen pixels to keep it even on every edge. 3px matches the no-go zone border weight. */
          vector-effect: non-scaling-stroke;
          stroke-width: 3px;
          stroke-dasharray: 6 5;
        `
      : css`
          stroke-width: ${$isSelected ? 0.8 : 1.2};
          stroke-dasharray: ${$isSelected ? 1.5 : 2.2};
        `}
    pointer-events: ${$disabled ? 'none' : 'all'};
    cursor: ${$disabled ? 'default' : 'pointer'};
    outline: none;
  `}
`;

/* Holds the selection number and the area name pill, 4px apart. */
export const CleaningAreaLabel = styled.div`
  position: absolute;
  transform: translate(-50%, -50%);
  display: flex;
  align-items: center;
  gap: 4px;
  max-width: 160px;
  pointer-events: none;
`;

/* Pill colours are one-off design values (selected / unselected / whole-map), not KCC tokens. */
const getAreaLabelBackground = (isSelected?: boolean, areaStyle?: CleaningAreaMapOverlayStyle): string => {
  if (areaStyle === 'wholeMap') {
    return 'rgba(22, 22, 22, 0.4)';
  }

  return isSelected ? 'rgba(22, 22, 22, 1)' : 'rgba(75, 137, 105, 1)';
};

export const CleaningAreaLabelText = styled(Typography.Text)<{
  $isSelected?: boolean;
  $areaStyle?: CleaningAreaMapOverlayStyle;
}>`
  ${({ theme: { kcc }, $isSelected, $areaStyle }): FlattenSimpleInterpolation => css`
    &.ant-typography {
      padding: 4px 8px;
      border-radius: 100px;
      background-color: ${getAreaLabelBackground($isSelected, $areaStyle)};
      font-size: 10px;
      font-weight: ${kcc.typography.FONT_WEIGHT.EXTRA_BOLD};
      color: #ffffff;
      white-space: normal;
      word-break: break-word;
      text-align: center;
    }
  `}
`;

export const CleaningAreaLabelBadge = styled(Badge)`
  ${({ theme: { kcc } }): FlattenSimpleInterpolation => css`
    flex-shrink: 0;

    /* Prefixed with .ant-badge so these beat antd's own, more specific badge-count rules. */
    &.ant-badge.ant-badge-not-a-wrapper .ant-badge-count {
      display: flex;
      align-items: center;
      justify-content: center;
      min-width: auto;
      height: auto;
      line-height: 10px;
      border-radius: 100px;
      padding: 4px 7px;
      box-shadow: none;
      background-color: rgba(22, 22, 22, 1);
      color: ${kcc.colors.GRAY_50};
      font-size: 10px;
      font-weight: ${kcc.typography.FONT_WEIGHT.EXTRA_BOLD};
    }

    /* Keeps the scrolling digit as tall as the 10px text so the padding sets the badge size. */
    &.ant-badge .ant-scroll-number-only {
      display: flex;
      align-items: center;
      height: 10px;

      > p.ant-scroll-number-only-unit {
        height: 10px;
        line-height: 10px;
      }
    }
  `}
`;
